import { APP } from '../config/app';
import { normalizePhoneForWhatsapp } from './phone';

/** Strips non-digits and ensures a clean 91 prefix for 10-digit Indian numbers. */
export const cleanPhoneForWhatsapp = (phone) => {
  if (phone === '' || phone === null) return '';
  const candidate = phone !== undefined ? phone : (APP.supportWhatsapp || APP.supportPhone || '');
  return normalizePhoneForWhatsapp(candidate);
};

export const buildWhatsappUrl = (message, phoneOverride) => {
  const phone = cleanPhoneForWhatsapp(phoneOverride);
  if (!phone) return '#';
  return `https://wa.me/${phone}?text=${encodeURIComponent(message || '')}`;
};

/** WhatsApp share — user picks a contact (not support). */
export const buildWhatsappShareUrl = (message) =>
  `https://wa.me/?text=${encodeURIComponent(message || '')}`;

/** WhatsApp to a specific phone (emergency contact). */
export const buildWhatsappToPhone = (phone, message) => {
  const digits = cleanPhoneForWhatsapp(phone);
  if (!digits) return '#';
  return `https://wa.me/${digits}?text=${encodeURIComponent(message || '')}`;
};

export const buildSmsUrl = (phone, message) => {
  const digits = String(phone || '').replace(/\D/g, '').slice(-10);
  if (digits.length < 10) return '#';
  return `sms:+91${digits}?body=${encodeURIComponent(message || '')}`;
};

export const buildTelUrl = (phoneOverride) => {
  const phone = phoneOverride || APP.supportPhone;
  const digits = String(phone || '').replace(/\D/g, '').slice(-10);
  return digits.length === 10 ? `tel:+91${digits}` : '#';
};

/** India national emergency number. */
export const buildSosTelUrl = () => 'tel:112';

/**
 * Universal WhatsApp Launcher:
 * Directly invokes the native WhatsApp application via the whatsapp:// scheme on mobile devices,
 * eliminating blank tabs, Chrome redirects, or web.whatsapp.com stalls.
 */
export const openWhatsappChat = (message, phoneOverride) => {
  const phone = cleanPhoneForWhatsapp(phoneOverride);
  const text = encodeURIComponent(message || '');

  const nativeUrl = phone
    ? `whatsapp://send?phone=${phone}&text=${text}`
    : `whatsapp://send?text=${text}`;

  const webUrl = phone
    ? `https://wa.me/${phone}?text=${text}`
    : `https://wa.me/?text=${text}`;

  const isNativeOrMobile = typeof window !== 'undefined' && (
    window.__MYTOWNLY_RIDES_NATIVE__ ||
    (window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform()) ||
    /Android|iPhone|iPad|iPod/i.test(navigator.userAgent || '')
  );

  if (isNativeOrMobile) {
    try {
      window.location.href = nativeUrl;
      return;
    } catch (e) {
      console.warn('Native whatsapp:// launch failed, falling back to webUrl', e);
    }
  }

  // Desktop web fallback
  try {
    const opened = window.open(webUrl, '_blank', 'noopener,noreferrer');
    if (!opened) window.location.href = webUrl;
  } catch {
    window.location.href = webUrl;
  }
};

/** System share first, then WhatsApp contact picker. Never routes to support. */
export const shareTripText = async (text, title = 'MyTownly Ride') => {
  try {
    openWhatsappChat(text, '');
    return 'whatsapp';
  } catch { /* fall through */ }

  if (typeof navigator !== 'undefined' && navigator.share) {
    try {
      await navigator.share({ title, text });
      return 'shared';
    } catch (e) {
      if (e?.name === 'AbortError') return 'cancelled';
    }
  }
  return 'whatsapp';
};

/** Fire-and-forget WhatsApp open (no await). */
export const openWhatsappShare = (text) => {
  openWhatsappChat(text, '');
};
